import { BellRing, MapPin, CalendarCheck2, ListChecks } from "lucide-react";
import { colourOf, fmtTime, weatherLook } from "./palette";
import { TravelWarning } from "./TravelWarning";

const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);

export const DayView = ({ day, events, reminders, tasks, holiday, weather, accent, clock, density, travel }) => {
  const timed = events.filter((e) => !e.all_day);
  const allDay = events.filter((e) => e.all_day);
  const sky = weather[day];
  const Look = sky ? weatherLook(sky.code) : null;
  const rowH = density === "compact" ? "min-h-[28px]" : "min-h-[40px]";
  const dayTasks = tasks.filter((t) => t.due === day && !t.done);

  return (
    <div className="mt-6 rounded-3xl border border-white/10 bg-panel p-5 sm:p-7" data-testid="day-view">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display text-sm font-bold tracking-tight">
          {new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
            weekday: "long", day: "numeric", month: "long", year: "numeric",
          })}
        </p>
        {Look && (
          <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft" title={Look.blurb}>
            <Look.Icon className="h-4 w-4" style={{ color: Look.tint }} /> {sky.max}° / {sky.min}°
            <span className="text-ink-soft">· {Look.blurb}</span>
          </span>
        )}
        {holiday && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-400/40 bg-violet-400/10 px-3 py-1 text-xs font-semibold text-violet-200"
                data-testid="day-view-holiday">
            <CalendarCheck2 className="h-3 w-3" /> {holiday}
          </span>
        )}
      </div>

      <TravelWarning warnings={travel} clock={clock} />

      {(allDay.length > 0 || reminders.length > 0 || dayTasks.length > 0) && (
        <div className="mt-3 space-y-1.5 rounded-xl border border-white/10 bg-page p-3" data-testid="day-view-allday">
          {allDay.map((ev) => (
            <p key={`${ev.event_id}-${ev.day}`} className="flex items-center gap-2 text-sm">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colourOf(ev.colour) }} />
              <span dir="auto" className="font-semibold">{ev.title}</span>
              <span className="text-xs text-ink-soft">All day</span>
              {ev.location && (
                <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                  <MapPin className="h-3 w-3" /> {ev.location}
                </span>
              )}
            </p>
          ))}
          {reminders.map((r) => (
            <p key={r.reminder_id} className="flex items-center gap-2 text-sm">
              <BellRing className="h-3.5 w-3.5" style={{ color: accent }} />
              <span dir="auto" className={r.done ? "text-ink-soft line-through" : ""}>{r.text}</span>
            </p>
          ))}
          {dayTasks.map((t) => (
            <p key={t.task_id} className="flex items-center gap-2 text-sm">
              <ListChecks className="h-3.5 w-3.5 text-ink-soft" />
              <span dir="auto">{t.text}</span>
              <span className="text-xs text-ink-soft">due today</span>
            </p>
          ))}
        </div>
      )}

      <div className="mt-3 divide-y divide-white/5" data-testid="day-view-hours">
        {HOURS.map((hour) => {
          const slot = timed.filter((e) => e.start.slice(0, 2) === hour.slice(0, 2));
          return (
            <div key={hour} className={`flex items-start gap-3 py-1 ${rowH}`} data-testid={`day-hour-${hour}`}>
              <span className="w-16 shrink-0 pt-1 text-[11px] tabular-nums text-ink-soft">
                {fmtTime(hour, clock)}
              </span>
              <span className="flex-1 space-y-1">
                {slot.map((ev) => (
                  <span
                    key={`${ev.event_id}-${ev.day}-${ev.start}`}
                    className="block rounded-lg px-2 py-1 text-xs font-semibold text-page"
                    style={{ backgroundColor: colourOf(ev.colour) }}
                  >
                    {fmtTime(ev.start, clock)}{ev.end ? `–${fmtTime(ev.end, clock)}` : ""} · {ev.title}
                    {ev.tz && ev.local_start ? ` (${fmtTime(ev.local_start, clock)} your time)` : ""}
                  </span>
                ))}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
